import { useEffect } from 'react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';

/**
 * Civic inline palette. The AHP-authored pages and components style their
 * civic branches with inline colours — Old Glory red, navy, presidential gold,
 * warm white and their dark-mode counterparts. Those literals were rewritten
 * to read these variables so a second civic brand (AntiGreed) can reuse the
 * same layouts with its own palette. AHP's values ARE the historical literals,
 * so AHP renders byte-for-byte as before; Luigi's gets the same values so the
 * handful of un-gated usages (footer gold, etc.) don't change either.
 */
type CivicPalette = Record<string, string>;

const AHP_CIVIC: CivicPalette = {
  red: '#B22234', 'red-soft': '#e85d6c',
  navy: '#002868', 'navy-soft': '#7da6d4', 'navy-deep': '#002147', 'navy-deeper': '#001238',
  'navy-panel': '#152240', 'navy-ink': '#0a1525',
  gold: '#C5A55A', 'gold-ink': '#7A5F1F',
  ink: '#1B1B1B', cream: '#FAF9F6', parchment: '#F5EBD7', warm: '#f0ebe3',
  money: '#2b5e49', 'money-soft': '#6bc9a0',
  'bg-dark': '#0a1628', 'card-dark': '#0f1d2f',
  muted: '#8899aa', 'muted-2': '#667788', blue: '#4A90D9',
};

// AntiGreed — deep green, cream, burnt orange. Each slot keeps the ROLE the
// AHP literal plays (ink = near-black text on light surfaces, cream = the
// light ground / dark-mode text, navy = the solid brand panel colour, …), so
// every guarded and unguarded usage stays readable in both schemes.
const ANTIGREED_CIVIC_DARK: CivicPalette = {
  red: '#ff7a2e', 'red-soft': '#ff9a5c',
  navy: '#1f5c37', 'navy-soft': '#8fd3ad', 'navy-deep': '#0c2a18', 'navy-deeper': '#07150a',
  'navy-panel': '#183a22', 'navy-ink': '#0b1a0c',
  gold: '#e8d4b0', 'gold-ink': '#6b4a10',
  ink: '#12200f', cream: '#f7f1e6', parchment: '#efe6d2', warm: '#f5e6d3',
  money: '#2b5e49', 'money-soft': '#6bc9a0',
  'bg-dark': '#0a1408', 'card-dark': '#122012',
  muted: '#a3b3a0', 'muted-2': '#7f907c', blue: '#5aa9d6',
};
const ANTIGREED_CIVIC_LIGHT: CivicPalette = {
  red: '#c94a00', 'red-soft': '#c94a00',
  navy: '#1a4d2e', 'navy-soft': '#2e7d4f', 'navy-deep': '#123a22', 'navy-deeper': '#0c2a18',
  'navy-panel': '#183a22', 'navy-ink': '#0b1a0c',
  gold: '#8a5a12', 'gold-ink': '#6b4a10',
  ink: '#12200f', cream: '#f7f1e6', parchment: '#efe6d2', warm: '#f5e6d3',
  money: '#2b5e49', 'money-soft': '#2b5e49',
  'bg-dark': '#0a1408', 'card-dark': '#122012',
  muted: '#5f6f5c', 'muted-2': '#7f907c', blue: '#1f6f9c',
};

/** Writes --civic-<name> and --civic-<name>-rgb ("r,g,b", for rgba() alpha
 *  variants and Tailwind's civic-*\/NN opacity modifiers). */
const setCivicPalette = (root: HTMLElement, palette: CivicPalette) => {
  for (const [name, hex] of Object.entries(palette)) {
    root.style.setProperty(`--civic-${name}`, hex);
    const h = hex.replace('#', '');
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    root.style.setProperty(`--civic-${name}-rgb`, `${r},${g},${b}`);
  }
};

/**
 * BrandTheme component that injects brand-specific CSS custom properties
 * This allows the existing CSS to work with both brands dynamically
 */
export const BrandTheme: React.FC = () => {
  const brand = useBrand();
  const { isDark } = useTheme();

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    if (brand.key === 'antigreed') {
      // AntiGreed.app — its own brand on AHP's civic layouts. Deep green,
      // cream and burnt orange, dark-first. A FULL shadcn token set for both
      // colour schemes, so no page falls through to Luigi's terminal defaults
      // in index.css (which is how the old eight-token theme produced white
      // headings on grey and neon-green tables).
      root.style.setProperty('--radius', '0.75rem');
      root.style.setProperty('--primary', isDark ? '24 100% 59%' : '22 100% 39%');       // #ff7a2e / #c94a00
      root.style.setProperty('--primary-foreground', isDark ? '110 43% 5%' : '0 0% 100%');
      root.style.setProperty('--destructive', isDark ? '14 100% 62%' : '10 80% 42%');
      root.style.setProperty('--destructive-foreground', isDark ? '110 43% 5%' : '0 0% 100%');
      root.style.setProperty('--blood-red', isDark ? '14 100% 62%' : '10 80% 42%');
      root.style.setProperty('--warning-amber', isDark ? '38 90% 62%' : '35 85% 38%');
      root.style.setProperty('--corruption-gray', isDark ? '110 10% 55%' : '110 8% 45%');

      if (isDark) {
        root.style.setProperty('--background', '110 43% 5%');        // #0a1408
        root.style.setProperty('--foreground', '34 63% 89%');        // #f5e6d3
        root.style.setProperty('--card', '113 30% 10%');             // #122012
        root.style.setProperty('--card-foreground', '34 63% 89%');
        root.style.setProperty('--popover', '113 30% 10%');
        root.style.setProperty('--popover-foreground', '34 63% 89%');
        root.style.setProperty('--secondary', '115 25% 14%');
        root.style.setProperty('--secondary-foreground', '34 63% 89%');
        root.style.setProperty('--muted', '115 20% 14%');
        root.style.setProperty('--muted-foreground', '110 12% 66%');
        root.style.setProperty('--accent', '115 25% 17%');
        root.style.setProperty('--accent-foreground', '34 63% 89%');
        root.style.setProperty('--border', '110 18% 22%');
        root.style.setProperty('--input', '115 25% 12%');
        root.style.setProperty('--ring', '24 100% 59%');
        // Luigi-era semantic names still used by shared components:
        // cream headings/links on the green ground.
        root.style.setProperty('--matrix-green', '40 55% 82%');
        root.style.setProperty('--terminal-green', '40 50% 75%');
        root.style.setProperty('--shadow-matrix', '0 2px 12px rgba(0,0,0,0.45)');
        root.style.setProperty('--shadow-corruption', '0 2px 12px rgba(255,122,46,0.18)');
        root.style.setProperty('--shadow-terminal', '0 2px 16px rgba(0,0,0,0.4)');
        root.style.setProperty('--gradient-matrix', 'linear-gradient(135deg, rgba(232,212,176,0.06), rgba(10,20,8,0.4))');
        root.style.setProperty('--gradient-corruption', 'linear-gradient(90deg, #0a1408, rgba(255,122,46,0.08), #0a1408)');
        root.style.setProperty('--gradient-terminal', 'linear-gradient(180deg, #122012, #0a1408)');
        setCivicPalette(root, ANTIGREED_CIVIC_DARK);
        body.style.background = 'radial-gradient(120% 80% at 50% 0%, #10261a 0%, #0a1408 55%, #070f06 100%) fixed';
        body.style.backgroundColor = '#0a1408';
      } else {
        root.style.setProperty('--background', '40 46% 95%');        // #f7f1e6 cream paper
        root.style.setProperty('--foreground', '113 36% 9%');        // #12200f
        root.style.setProperty('--card', '40 50% 98%');
        root.style.setProperty('--card-foreground', '113 36% 9%');
        root.style.setProperty('--popover', '40 50% 98%');
        root.style.setProperty('--popover-foreground', '113 36% 9%');
        root.style.setProperty('--secondary', '40 30% 90%');
        root.style.setProperty('--secondary-foreground', '113 36% 9%');
        root.style.setProperty('--muted', '40 30% 90%');
        root.style.setProperty('--muted-foreground', '115 15% 36%');
        root.style.setProperty('--accent', '120 25% 90%');
        root.style.setProperty('--accent-foreground', '113 36% 9%');
        root.style.setProperty('--border', '40 20% 82%');
        root.style.setProperty('--input', '40 25% 91%');
        root.style.setProperty('--ring', '22 100% 39%');
        root.style.setProperty('--matrix-green', '145 50% 24%');     // forest green
        root.style.setProperty('--terminal-green', '145 40% 30%');
        root.style.setProperty('--shadow-matrix', '0 2px 12px rgba(26,77,46,0.08)');
        root.style.setProperty('--shadow-corruption', '0 2px 12px rgba(201,74,0,0.1)');
        root.style.setProperty('--shadow-terminal', '0 2px 16px rgba(0,0,0,0.06)');
        root.style.setProperty('--gradient-matrix', 'linear-gradient(135deg, rgba(26,77,46,0.04), rgba(26,77,46,0.01))');
        root.style.setProperty('--gradient-corruption', 'linear-gradient(90deg, #f7f1e6, rgba(201,74,0,0.05), #f7f1e6)');
        root.style.setProperty('--gradient-terminal', 'linear-gradient(180deg, #fbf7ee, #f7f1e6)');
        setCivicPalette(root, ANTIGREED_CIVIC_LIGHT);
        body.style.background = 'radial-gradient(120% 80% at 50% 0%, #fbf7ee 0%, #f7f1e6 55%, #f1e9d8 100%) fixed';
        body.style.backgroundColor = '#f7f1e6';
      }
      body.className = body.className.replace(/\bdark\b/g, '').replace(/\bbrand-\w+\b/g, '').trim();
      body.classList.add('brand-antigreed', isDark ? 'brand-antigreed-dark' : 'brand-antigreed-light');

      updateFavicon('/antigreed-icon.png');
      updateAppleTouchIcon('/antigreed-icon.png');
      updateMetaThemeColor(isDark ? '#0a1408' : '#f7f1e6');
    } else if (brand.key === 'ahp') {
      // American Hospital Prices — Patriotic red/white/blue theme (light) or dark navy (dark mode)
      // Green used ONLY for positive money values via inline styles

      // Core brand colors — always the same regardless of mode
      setCivicPalette(root, AHP_CIVIC);
      root.style.setProperty('--blood-red', '355 69% 42%'); // Old Glory Red #B22234
      root.style.setProperty('--terminal-green', '155 36% 35%'); // Money green only
      root.style.setProperty('--corruption-gray', '0 0% 60%');
      root.style.setProperty('--warning-amber', '42 38% 53%'); // Presidential gold #C5A55A

      // Radius
      root.style.setProperty('--radius', '0.5rem');

      // Primary — Old Glory Red (always)
      root.style.setProperty('--primary', '355 69% 42%'); // #B22234
      root.style.setProperty('--primary-foreground', '0 0% 100%');

      // Destructive — same red
      root.style.setProperty('--destructive', '355 69% 42%');
      root.style.setProperty('--destructive-foreground', '0 0% 100%');

      if (isDark) {
        // ── AHP DARK MODE — navy theme matching the loading skeleton ──────
        root.style.setProperty('--matrix-green', '42 38% 53%');     // Gold as primary UI color
        root.style.setProperty('--background', '216 60% 10%');       // #0a1628 navy
        root.style.setProperty('--foreground', '40 33% 96%');        // warm white
        root.style.setProperty('--card', '217 55% 14%');             // slightly lighter navy
        root.style.setProperty('--card-foreground', '40 33% 96%');
        root.style.setProperty('--popover', '217 55% 14%');
        root.style.setProperty('--popover-foreground', '40 33% 96%');
        root.style.setProperty('--secondary', '217 45% 18%');
        root.style.setProperty('--secondary-foreground', '40 33% 96%');
        root.style.setProperty('--muted', '217 40% 16%');
        // 55% lightness failed AA on the dark cards (4.1:1 at small sizes); 70% clears it.
        root.style.setProperty('--muted-foreground', '217 22% 70%');
        root.style.setProperty('--accent', '217 45% 18%');
        root.style.setProperty('--accent-foreground', '40 33% 96%');
        root.style.setProperty('--border', '217 30% 22%');
        root.style.setProperty('--input', '217 45% 18%');
        root.style.setProperty('--ring', '42 38% 53%');              // gold focus ring

        root.style.setProperty('--shadow-matrix', '0 2px 12px rgba(10,22,40,0.6)');
        root.style.setProperty('--shadow-corruption', '0 2px 12px rgba(178,34,52,0.2)');
        root.style.setProperty('--shadow-terminal', '0 2px 16px rgba(0,0,0,0.4)');
        root.style.setProperty('--gradient-matrix', 'linear-gradient(135deg, rgba(197,165,90,0.06), rgba(10,22,40,0.4))');
        root.style.setProperty('--gradient-corruption', 'linear-gradient(90deg, #0a1628, rgba(178,34,52,0.08), #0a1628)');
        root.style.setProperty('--gradient-terminal', 'linear-gradient(180deg, #0d1e35, #0a1628)');

        body.style.background = '';
        body.style.backgroundColor = '#0a1628';
        body.className = body.className.replace(/\bdark\b/g, '').replace(/\bbrand-\w+\b/g, '').trim();
        body.classList.add('brand-ahp', 'brand-ahp-dark');
      } else {
        // ── AHP LIGHT MODE — warm white / patriotic palette ──────────────
        root.style.setProperty('--matrix-green', '217 100% 20%');    // Navy #002868 for UI elements
        root.style.setProperty('--background', '40 20% 98%');         // Warm white #FAF9F6
        root.style.setProperty('--foreground', '0 0% 11%');           // Near-black #1B1B1B
        root.style.setProperty('--card', '0 0% 100%');
        root.style.setProperty('--card-foreground', '0 0% 11%');
        root.style.setProperty('--popover', '0 0% 100%');
        root.style.setProperty('--popover-foreground', '0 0% 11%');
        root.style.setProperty('--secondary', '40 15% 94%');
        root.style.setProperty('--secondary-foreground', '0 0% 11%');
        root.style.setProperty('--muted', '40 15% 94%');
        root.style.setProperty('--muted-foreground', '0 0% 35%');
        root.style.setProperty('--accent', '355 30% 96%');
        root.style.setProperty('--accent-foreground', '0 0% 11%');
        root.style.setProperty('--border', '40 10% 88%');
        root.style.setProperty('--input', '40 10% 93%');
        root.style.setProperty('--ring', '355 69% 42%');              // Red focus ring

        root.style.setProperty('--shadow-matrix', '0 2px 12px rgba(0,40,104,0.08)');
        root.style.setProperty('--shadow-corruption', '0 2px 12px rgba(178,34,52,0.1)');
        root.style.setProperty('--shadow-terminal', '0 2px 16px rgba(0,0,0,0.06)');
        root.style.setProperty('--gradient-matrix', 'linear-gradient(135deg, rgba(0,40,104,0.04), rgba(0,40,104,0.01))');
        root.style.setProperty('--gradient-corruption', 'linear-gradient(90deg, #FAF9F6, rgba(178,34,52,0.05), #FAF9F6)');
        root.style.setProperty('--gradient-terminal', 'linear-gradient(180deg, #FFFFFF, #FAF9F6)');

        body.style.background = '';
        body.style.backgroundColor = '#FAF9F6';
        body.className = body.className.replace(/\bdark\b/g, '').replace(/\bbrand-\w+\b/g, '').trim();
        body.classList.add('brand-ahp');
      }

      // Update favicon and app icon
      updateFavicon('/consumer/favicon.svg');
      updateAppleTouchIcon('/consumer/favicon.svg');
      updateMetaThemeColor('#B22234'); // Old Glory Red for mobile browser chrome
    } else {
      // Luigi's Hall of Shame - Original matrix green theme
      root.style.setProperty('--matrix-green', '120 100% 50%'); // Matrix green in HSL
      root.style.setProperty('--blood-red', '0 100% 50%'); // Blood red in HSL
      root.style.setProperty('--terminal-green', '120 100% 80%'); // Terminal green in HSL
      root.style.setProperty('--primary', '120 100% 50%'); // Green primary
      root.style.setProperty('--destructive', '0 100% 50%'); // Red destructive
      root.style.setProperty('--background', '0 0% 4%'); // Deep black
      root.style.setProperty('--border', '120 30% 20%'); // Green borders
      root.style.setProperty('--ring', '120 100% 50%'); // Green focus rings

      // Set standard background for Luigi's
      body.style.background = '';
      body.style.backgroundColor = 'hsl(var(--background))';
      body.classList.remove('brand-ahp', 'brand-antigreed', 'brand-antigreed-dark', 'brand-antigreed-light');
      // Shared un-gated usages (footer gold, etc.) keep their historical values.
      setCivicPalette(root, AHP_CIVIC);

      // Update favicon and app icon for Luigi's
      updateFavicon('/src/assets/app-icon.png');
      updateAppleTouchIcon('/src/assets/app-icon.png');
      updateMetaThemeColor('#000000'); // Black
    }
  }, [brand.key, isDark]);

  // Helper function to update favicon
  const updateFavicon = (iconPath: string) => {
    // Update or create favicon link tags
    let icon16 = document.querySelector('link[rel="icon"][sizes="16x16"]') as HTMLLinkElement;
    let icon32 = document.querySelector('link[rel="icon"][sizes="32x32"]') as HTMLLinkElement;
    let iconDefault = document.querySelector('link[rel="icon"]:not([sizes])') as HTMLLinkElement;

    if (!icon16) {
      icon16 = document.createElement('link');
      icon16.rel = 'icon';
      icon16.type = 'image/png';
      icon16.sizes = '16x16';
      document.head.appendChild(icon16);
    }
    if (!icon32) {
      icon32 = document.createElement('link');
      icon32.rel = 'icon';
      icon32.type = 'image/png';
      icon32.sizes = '32x32';
      document.head.appendChild(icon32);
    }
    if (!iconDefault) {
      iconDefault = document.createElement('link');
      iconDefault.rel = 'icon';
      iconDefault.type = 'image/png';
      document.head.appendChild(iconDefault);
    }

    icon16.href = iconPath;
    icon32.href = iconPath;
    iconDefault.href = iconPath;
  };

  // Helper function to update apple touch icon
  const updateAppleTouchIcon = (iconPath: string) => {
    let appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement;

    if (!appleTouchIcon) {
      appleTouchIcon = document.createElement('link');
      appleTouchIcon.rel = 'apple-touch-icon';
      document.head.appendChild(appleTouchIcon);
    }

    appleTouchIcon.href = iconPath;
  };

  // Helper function to update meta theme color
  const updateMetaThemeColor = (color: string) => {
    let themeColorMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;

    if (!themeColorMeta) {
      themeColorMeta = document.createElement('meta');
      themeColorMeta.name = 'theme-color';
      document.head.appendChild(themeColorMeta);
    }

    themeColorMeta.content = color;
  };

  return null; // This is a utility component that doesn't render anything
};

/**
 * Hook to get brand-specific color values for inline styles
 */
export const useBrandColors = () => {
  const brand = useBrand();

  return {
    primary: brand.colors.primary,
    secondary: brand.colors.secondary,
    accent: brand.colors.accent,
    text: brand.colors.text,
    background: brand.colors.background,
    // CSS custom property references for use with className
    cssVars: {
      matrixGreen: 'var(--matrix-green)',
      bloodRed: 'var(--blood-red)',
      terminalGreen: 'var(--terminal-green)'
    }
  };
};